import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/auth'
import { officexLogin, emailLogin, register } from '../api/client'
import LoadingButton from '../components/LoadingButton'
import ErrorMessage from '../components/ErrorMessage'

export default function Landing() {
  const navigate = useNavigate()
  const { isAuthenticated, login } = useAuthStore()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const loginAttempted = useRef(false)

  // Email/Password form state
  const [mode, setMode] = useState<'login' | 'register'>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  useEffect(() => {
    // Parse URL params
    const params = new URLSearchParams(window.location.search)
    const installId = params.get('officex_install_id')
    const installSecret = params.get('officex_install_secret')
    const customerId = params.get('officex_customer_id')

    // If URL has OfficeX params, always attempt login (handles secret rotation)
    if (installId && installSecret && !loginAttempted.current) {
      loginAttempted.current = true
      handleAutoLogin(installId, installSecret, customerId || undefined)
      return
    }

    // If already authenticated (no URL params), redirect to jobs
    if (isAuthenticated) {
      navigate('/jobs')
    }
  }, [isAuthenticated, navigate])

  async function handleAutoLogin(installId: string, installSecret: string, customerId?: string) {
    setLoading(true)
    setError(null)

    try {
      const response = await officexLogin(installId, installSecret, customerId)

      login({
        userId: response.user_id,
        apiKey: response.api_key,
        installId,
        installSecret,
        email: response.email,
        username: response.username,
        creditsBalance: response.credits_balance,
        nocodbJobsViewUrl: response.nocodb_jobs_view_url,
        nocodbScheduledViewUrl: response.nocodb_scheduled_view_url,
      })
      navigate('/jobs')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed'
      setError(`Login failed: ${message}. Please try refreshing or contact support.`)
    } finally {
      setLoading(false)
    }
  }

  async function handleEmailLogin(e: React.FormEvent) {
    e.preventDefault()
    if (!email || !password) return

    setLoading(true)
    setError(null)

    try {
      const response = await emailLogin(email, password)

      login({
        userId: response.user_id,
        apiKey: response.api_key,
        email: response.email,
        username: response.username,
        creditsBalance: response.credits_balance,
        nocodbJobsViewUrl: response.nocodb_jobs_view_url,
        nocodbScheduledViewUrl: response.nocodb_scheduled_view_url,
      })
      navigate('/jobs')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  async function handleRegister(e: React.FormEvent) {
    e.preventDefault()
    if (!email || !password) return

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await register(email, password)

      login({
        userId: response.user_id,
        apiKey: response.api_key,
        email: response.email,
      })
      navigate('/jobs')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed')
    } finally {
      setLoading(false)
    }
  }

  if (loading && loginAttempted.current) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Logging in...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-600">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="text-5xl mb-3">📸</div>
            <h1 className="text-3xl font-bold text-gray-900">Daily Instagram Story</h1>
            <p className="mt-2 text-gray-600">AI-Generated Content for Your Stories</p>
          </div>

          {error && (
            <ErrorMessage
              message={error}
              className="mb-6"
              onDismiss={() => setError(null)}
            />
          )}

          {/* Email/Password Form */}
          <form onSubmit={mode === 'login' ? handleEmailLogin : handleRegister} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {mode === 'register' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            )}

            <LoadingButton
              type="submit"
              loading={loading}
              loadingText={mode === 'login' ? 'Logging in...' : 'Creating account...'}
              disabled={!email || !password}
              variant="primary"
              className="w-full py-3"
            >
              {mode === 'login' ? 'Log In' : 'Create Account'}
            </LoadingButton>
          </form>

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => {
                setMode(mode === 'login' ? 'register' : 'login')
                setError(null)
              }}
              className="text-sm text-indigo-600 hover:text-indigo-700"
            >
              {mode === 'login'
                ? "Don't have an account? Register"
                : 'Already have an account? Log in'}
            </button>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="text-center">
              <h3 className="font-medium text-gray-900 mb-2">OfficeX Users</h3>
              <p className="text-sm text-gray-600 mb-4">
                Install from the OfficeX App Store for automatic login.
              </p>
              <a
                href="https://officex.app/store"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-700"
              >
                Visit OfficeX App Store
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
